import { IComboBoxOption, SelectableOptionMenuItemType } from '@fluentui/react';
import { IVerticalStackedChartProps, IVSChartDataPoint, VerticalStackedBarChart } from '@fluentui/react-charting';
import AppContext from 'App/AppContext';
import { IPSpinner, IPText } from 'baseComponents';
import { IPComboBox } from 'baseComponents/Combobox/IPComboBox';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { PickerTenant, TenantPickerMode } from 'components/Pickers/TenantPicker';
import { globalStackTokensGapMedium } from 'globalStyles';
import AdminTenant from 'models/adminTenant';
import { Approval, ApprovalState, ApprovalStateColor } from 'models/approval';
import { ListsPerTenant, Widget72ResultDTO } from 'models/dto/widgets/widget72DTO';
import { isSameDirectory } from 'models/identityProvider';
import ResourceList, { ResourceListType } from 'models/resourceList';
import Tenant from 'models/tenant';
import { isPartnerWidgetType, WidgetSizes, WidgetWidth } from 'models/widget';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTenantListApprovalStatus } from 'services/Api/dashboardService';
import { apiGetListsForTenants } from 'services/Api/listService';
import { apiGetTenantsForPartner } from 'services/Api/partnerService';
import { apiGetTenantsForUser } from 'services/Api/tenantService';
import { apiRequest } from 'services/Auth/authConfig';
import { enumParseKey } from 'utils/object';
import { sortOnString } from 'utils/sorting';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';
import {
  getTenantWidgetTenantById,
  getTenantWidgetTenantId,
  getTenantWidgetTenantName,
  sortTenantWidgetTenantsByName,
  TenantWidgetTenant,
  WithTenantId,
  withTenantId,
} from './tenantWidgetData';
import { useTenantDrillDown } from './useTenantDrillDown';

export class WidgetTenantListApprovalStatusConfig {
  lists: ListsPerTenant = {}; //lists per tenant

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.lists = newRawConfig.lists ?? {};
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTenantListApprovalStatusConfig {
    const newConfig = new WidgetTenantListApprovalStatusConfig();
    newConfig.lists = { ...this.lists };

    return newConfig;
  }
}

interface IWidgetTenantListApprovalStatusProps extends IWidgetRendererProps {}

const WidgetTenantListApprovalStatus = (props: IWidgetTenantListApprovalStatusProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<Widget72ResultDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tenants, setTenants] = useState<TenantWidgetTenant[]>([]);
  const [tenantsToShow, setTenantsToShow] = useState<TenantWidgetTenant[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();

        const tenants: TenantWidgetTenant[] = isPartnerWidgetType(props.widget.widgetType)
          ? props.sharedData?.partnerCustomers ?? []
          : props.sharedData?.userOrgUnits ?? [];
        setTenants(tenants);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);

        const data = await apiGetWidgetDataTenantListApprovalStatus(config.lists, accessToken, props.abortSignal);

        setData(data);

        const tenantsToShow = tenants.filter((tenant) =>
          data.some((s) => s.tenantId === getTenantWidgetTenantId(tenant)),
        );
        sortTenantWidgetTenantsByName(tenantsToShow);
        setTenantsToShow(tenantsToShow);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData?.partnerCustomers, props.sharedData?.userOrgUnits]);

  const loadConfig = (): WidgetTenantListApprovalStatusConfig => {
    const newConfig = new WidgetTenantListApprovalStatusConfig();
    newConfig.load(props.widget.widgetConfig);

    return newConfig;
  };

  const getData = (): IVerticalStackedChartProps[] => {
    if (data.length === 0 || tenantsToShow.length === 0) return [];

    const graphData: WithTenantId<IVerticalStackedChartProps>[] = tenantsToShow.map((tenant) => {
      const tenantId = getTenantWidgetTenantId(tenant);
      const tenantData = data.find((s) => s.tenantId === tenantId);
      const tenantName = getTenantWidgetTenantName(tenant);
      const dataPoints: IVSChartDataPoint[] = [];

      if (tenantData?.data) {
        const states = Object.entries(tenantData.data);
        states.sort(
          (a, b) =>
            ApprovalState[a[0] as keyof typeof ApprovalState] - ApprovalState[b[0] as keyof typeof ApprovalState],
        ); //sort by enum value

        for (const [key, value] of states) {
          const state = enumParseKey(ApprovalState, key);
          if (state === undefined) continue;
          dataPoints.push({
            legend: Approval.getStatusText(state, t),
            data: value,
            color: ApprovalStateColor[state],
            xAxisCalloutData: tenantName,
          });
        }
      }

      return {
        chartData: withTenantId(dataPoints, tenantId),
        xAxisPoint: tenantName,
        tenantId,
      };
    });

    return graphData;
  };

  const getFilterForColor = (data: IVSChartDataPoint): string => {
    const statusEntry = Object.entries(ApprovalStateColor).find(([_, color]) => color === data.color);
    if (!statusEntry) return '';

    return `approvalStatus=${statusEntry[0]}`;
  };

  const drillDown = useTenantDrillDown();

  const onClick = (data: IVSChartDataPoint) => {
    const tenant = getTenantWidgetTenantById(tenants, (data as WithTenantId<IVSChartDataPoint>).tenantId);
    if (!tenant) return;
    const filter = getFilterForColor(data);
    let url = '/library/links';
    if (filter) {
      url += `?${filter}`;
    }
    drillDown(tenant, url, isPartnerWidgetType(props.widget.widgetType));
  };

  //
  // Main render
  //
  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!isLoading && (data.length === 0 || tenantsToShow.length === 0)) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  const shouldRotateXAxisLabels = tenantsToShow.length > 10 && props.widget.widgetSize !== WidgetSizes.Large;

  return (
    <IPStack verticalFill>
      <IPStack.Item grow>
        <VerticalStackedBarChart
          data={getData()}
          height={250}
          width={WidgetWidth[props.widget.widgetSize] - 80}
          wrapXAxisLables={!shouldRotateXAxisLabels}
          rotateXAxisLables={shouldRotateXAxisLabels}
          barWidth={'auto'}
          legendProps={{
            centerLegends: true,
          }}
          onBarClick={(ev, data) => onClick(data as IVSChartDataPoint)}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetTenantListApprovalStatus;

//
// Config
//
interface IWidgetConfigWidgetTenantListApprovalStatusProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTenantListApprovalStatus = (props: IWidgetConfigWidgetTenantListApprovalStatusProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'widgets']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [tenants, setTenants] = useState<PickerTenant[]>([]);
  const [lists, setLists] = useState<ResourceList[]>([]);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTenantListApprovalStatusConfig => {
    let newRawConfig = new WidgetTenantListApprovalStatusConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
    }

    return newRawConfig;
  };

  const isOrgUnit = (tenant: Tenant): boolean => {
    return isSameDirectory(tenant, appContext.user.tenant);
  };

  const getKey = (tenantId: string | undefined, listId: number | undefined): string => {
    return `${tenantId}_${listId}`;
  };

  const getKeys = (lists: ListsPerTenant): string[] => {
    return Object.keys(lists)
      .map((tenantId) => lists[tenantId].map((listId) => getKey(tenantId, listId)))
      .flat();
  };

  const getListsPerTenant = (keys: string[]): ListsPerTenant => {
    const listsPerTenant: ListsPerTenant = {};

    for (const key of keys) {
      const parts = key.split('_');
      if (parts.length !== 2) continue;
      const tenantId = parts[0];
      const listId = parseInt(parts[1]);
      if (isNaN(listId)) continue;

      if (!listsPerTenant[tenantId]) {
        listsPerTenant[tenantId] = [];
      }
      listsPerTenant[tenantId].push(listId);
    }

    return listsPerTenant;
  };

  const updateLists = (lists: string[]) => {
    setSelectedLists(lists);
    const config = getConfig(lists);
    props.onUpdateConfig(JSON.stringify(config), lists.length > 0);
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const config = loadConfig();

      //for each key in the config, create a composite key of tenantId_listId
      setSelectedLists(getKeys(config.lists));

      const mode = isPartnerWidgetType(props.widget.widgetType) ? TenantPickerMode.Partner : TenantPickerMode.User;

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let tenants: PickerTenant[] = [];

      if (mode === TenantPickerMode.Partner) {
        //Show all customers for the partner
        const partnerTenants = await apiGetTenantsForPartner(accessToken, appContext.globalDataCache);
        tenants = partnerTenants.map((tenant: AdminTenant) => {
          const pickerTenant = new PickerTenant();
          pickerTenant.tenantId = tenant.tenantId;
          pickerTenant.name = tenant.displayName || tenant.name;

          return pickerTenant;
        });
      } else {
        //Show organizational units for the user
        const userTenants = await apiGetTenantsForUser(accessToken);
        tenants = userTenants
          .filter((t) => isOrgUnit(t))
          .map((tenant: Tenant) => {
            const pickerTenant = new PickerTenant();
            pickerTenant.tenantId = tenant.id;
            pickerTenant.name = tenant.name;

            return pickerTenant;
          });
      }
      tenants.sort((a, b) => sortOnString(a.name, b.name));

      let lists = await apiGetListsForTenants(
        tenants.map((t) => t.tenantId),
        accessToken,
      );

      lists = lists.filter(
        (l) =>
          tenants.some((t) => t.tenantId === l.tenantId) &&
          (l.listType === ResourceListType.DocumentLibrary || l.listType === ResourceListType.SitePageLibrary),
      );

      tenants = tenants.filter((t) => lists.some((l) => l.tenantId === t.tenantId));

      setTenants(tenants);
      setLists(lists);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (lists: string[]): WidgetTenantListApprovalStatusConfig => {
    const config = new WidgetTenantListApprovalStatusConfig();
    config.lists = getListsPerTenant(lists);

    return config;
  };

  const getOptions = (): IComboBoxOption[] => {
    const options: IComboBoxOption[] = [
      {
        key: '-1',
        text: t('widgets:Tenant.Select.SelectAll'),
        itemType: SelectableOptionMenuItemType.SelectAll,
      },
      {
        key: '0',
        text: '-',
        itemType: SelectableOptionMenuItemType.Divider,
      },
    ];

    //create a header for each tenant and add the lists as options
    for (const tenant of tenants) {
      const listsForTenant = lists
        .filter((l) => l.tenantId === tenant.tenantId)
        .sort((a, b) => sortOnString(a.name, b.name));

      if (listsForTenant.length === 0) continue;

      options.push({
        key: tenant.tenantId,
        text: tenant.name,
        itemType: SelectableOptionMenuItemType.Header,
      });

      for (const list of listsForTenant) {
        options.push({
          key: `${tenant.tenantId}_${list.listId}`,
          text: list.name,
          data: list,
        });
      }
    }

    return options;
  };

  const getSelectedKeys = (): string[] => {
    return [selectedLists.length === lists.length ? '-1' : '0', ...selectedLists];
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPComboBox
          useComboBoxAsMenuWidth
          autoComplete={'on'}
          multiSelect={true}
          options={getOptions()}
          placeholder={t('widgets:TenantListApprovalStatusOrgUnit.Config.SelectListsPlaceholder')}
          selectedKey={getSelectedKeys()}
          label={t('widgets:TenantListApprovalStatusOrgUnit.Config.SelectListsLabel')}
          onChange={(ev, option) => {
            if (option) {
              let selected: string[] = [];
              if (option?.itemType === SelectableOptionMenuItemType.SelectAll) {
                const allSelected = selectedLists.length === lists.length;
                selected = allSelected ? [] : lists.map((a) => getKey(a.tenantId, a.listId));
              } else {
                selected = option.selected
                  ? [...selectedLists, option.key as string]
                  : selectedLists.filter((t) => t !== option.key);
              }
              updateLists(selected);
            }
          }}
          styles={{ optionsContainer: { maxHeight: '40vh' } }}
          text={t('translation:General.Notifications.ItemCount', { count: selectedLists.length })}
        />
      </IPStack.Item>
    </IPStack>
  );
};
